import { useEffect } from 'react';
import { faIdBadge, faTrashCan, faRotateRight, faLock, faPencil } from '@fortawesome/pro-regular-svg-icons';
import { IconTag, NotificationDispatch } from '@tryhackme/thm-ui-components';
import { faCircleSmall } from '@fortawesome/pro-solid-svg-icons';
import { useFeatureFlag } from 'src/common/hooks';
import { FeatureFlagName } from 'src/common/constants';
import { formatDate } from '../../../../../common/date';
import { UpdateTokenModal } from '../update-token-modal';
import { UpdatePlanModal } from '../update-plan-modal';
import { SidePanelSelfServeRow } from '../self-serve-tag';
import { Toggler } from '../toggle-state';
import { AllowedToggleModes, SidePanelProps } from '../../corporate.types';
import { AddInstructorModal } from '../add-instructor-modal';
import { RenewPlansModal } from '../renew-plans-modal';
import { StyledSidePanelFormToggleRow, StyledSidePanelText } from '../../corporate.styles';
import { StyledCircleIcon } from '../self-serve-tag/self-serve-tag.styles';
import { EnableSSOModal } from '../enable-sso-modal';
import { UpdateCompanyDetailsModal } from '../update-company-details-modal';
import { StyledAvatar, StyledAvatarWrapper, StyledCompanyID, StyledCompanyNameWrapper, StyledUsername, CorpSidePanel, StyledCompanyIdLabel, StyledButtonColumn, StyledContentColumn, StyledInstructorButton } from './side-panel.styles';
export const SidePanel = ({
  companyId,
  companyLogo,
  companyWebsite,
  companyName,
  companyType,
  username,
  expiryDate,
  selfServeBilling,
  selfServeRenewal,
  pathActive,
  apiKeys,
  tokens,
  ssoEnabled,
  socSimEnabled
}: SidePanelProps) => {
  useEffect(() => {
    if (!expiryDate) {
      NotificationDispatch('error', 'Failed to retrieve expiry date.');
    }
  }, [expiryDate]);
  const {
    enabled: isSSOFlagEnabled
  } = useFeatureFlag(FeatureFlagName.SINGLE_SIGN_ON);
  return <CorpSidePanel data-sentry-element="CorpSidePanel" data-sentry-component="SidePanel" data-sentry-source-file="sidepanel.tsx">
      <StyledContentColumn data-sentry-element="StyledContentColumn" data-sentry-source-file="sidepanel.tsx">
        <StyledAvatarWrapper data-sentry-element="StyledAvatarWrapper" data-sentry-source-file="sidepanel.tsx">
          <StyledAvatar src={companyLogo} alt={username} data-sentry-element="StyledAvatar" data-sentry-source-file="sidepanel.tsx" />
          <StyledCompanyNameWrapper data-sentry-element="StyledCompanyNameWrapper" data-sentry-source-file="sidepanel.tsx">
            <StyledUsername data-sentry-element="StyledUsername" data-sentry-source-file="sidepanel.tsx">{username}</StyledUsername>
            <StyledCompanyID data-sentry-element="StyledCompanyID" data-sentry-source-file="sidepanel.tsx">{companyId}</StyledCompanyID>
          </StyledCompanyNameWrapper>
        </StyledAvatarWrapper>
        <StyledCompanyIdLabel data-sentry-element="StyledCompanyIdLabel" data-sentry-source-file="sidepanel.tsx">Expiry date: {expiryDate && formatDate(expiryDate)}</StyledCompanyIdLabel>
        <SidePanelSelfServeRow label="Self-serve billing" selfServeBilling={selfServeBilling} selfServeRenewal={selfServeRenewal ?? false} data-sentry-element="SidePanelSelfServeRow" data-sentry-source-file="sidepanel.tsx" />
        <SidePanelSelfServeRow label="Self-serve renewal" selfServeBilling={selfServeBilling} selfServeRenewal={selfServeRenewal ?? false} data-sentry-element="SidePanelSelfServeRow" data-sentry-source-file="sidepanel.tsx" />
        {isSSOFlagEnabled && ssoEnabled && <StyledSidePanelFormToggleRow>
            <StyledSidePanelText>SSO</StyledSidePanelText>
            <IconTag type="success">
              <StyledCircleIcon icon={faCircleSmall} $color="success" />
              Enabled
            </IconTag>
          </StyledSidePanelFormToggleRow>}
        <Toggler toggleStatus={apiKeys} companyId={companyId} mode={AllowedToggleModes.API_KEYS} data-sentry-element="Toggler" data-sentry-source-file="sidepanel.tsx">
          API keys
        </Toggler>
        <Toggler toggleStatus={pathActive} companyId={companyId} mode={AllowedToggleModes.PATHS} data-sentry-element="Toggler" data-sentry-source-file="sidepanel.tsx">
          Custom paths
        </Toggler>
        <Toggler toggleStatus={socSimEnabled} companyId={companyId} mode={AllowedToggleModes.SOC_SIM} data-sentry-element="Toggler" data-sentry-source-file="sidepanel.tsx">
          SOC Simulator
        </Toggler>
      </StyledContentColumn>
      <StyledButtonColumn data-sentry-element="StyledButtonColumn" data-sentry-source-file="sidepanel.tsx">
        <div>
          {isSSOFlagEnabled && !ssoEnabled && <EnableSSOModal companyId={companyId} icon={faLock} />}
          <UpdateTokenModal title="Add Tokens" numberOfTokens={tokens} buttonTitle="Add tokens" icon={faIdBadge} companyId={companyId} isAddToken data-sentry-element="UpdateTokenModal" data-sentry-source-file="sidepanel.tsx" />
          <UpdateTokenModal title="Remove Tokens" numberOfTokens={tokens} buttonTitle="Remove tokens" icon={faTrashCan} companyId={companyId} data-sentry-element="UpdateTokenModal" data-sentry-source-file="sidepanel.tsx" />
          <StyledInstructorButton data-sentry-element="StyledInstructorButton" data-sentry-source-file="sidepanel.tsx">
            <AddInstructorModal width="35" companyId={companyId} data-sentry-element="AddInstructorModal" data-sentry-source-file="sidepanel.tsx" />
          </StyledInstructorButton>
          <UpdatePlanModal icon={faRotateRight} companyId={companyId} data-sentry-element="UpdatePlanModal" data-sentry-source-file="sidepanel.tsx" />
          <RenewPlansModal icon={faRotateRight} companyId={companyId} data-sentry-element="RenewPlansModal" data-sentry-source-file="sidepanel.tsx" />
          <UpdateCompanyDetailsModal logo={companyLogo} companyId={companyId} title="Update company details" buttonTitle="Update company details" icon={faPencil} updateButtonText="Update Details" defaultValues={{
          companyName,
          url: companyWebsite,
          type: companyType
        }} data-sentry-element="UpdateCompanyDetailsModal" data-sentry-source-file="sidepanel.tsx" />
        </div>
      </StyledButtonColumn>
    </CorpSidePanel>;
};